import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, MenuItem, Alert,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { clientsApi } from '../../api/clients.api';
import type { Project, CreateProjectInput, ProjectStatus } from '../../api/projects.api';

interface Props {
  open: boolean;
  initial?: Project | null;
  onClose: () => void;
  onSubmit: (input: CreateProjectInput) => Promise<void>;
  error?: string | null;
}

const STATUSES: ProjectStatus[] = ['planned', 'active', 'on_hold', 'completed', 'closed', 'cancelled'];

export function ProjectFormDialog({ open, initial, onClose, onSubmit, error }: Props) {
  const isEdit = Boolean(initial);
  const { data: clients } = useQuery({ queryKey: ['clients', 'all'], queryFn: () => clientsApi.list({ limit: 100, status: 'active' }) });

  const [form, setForm] = useState<CreateProjectInput>({ projectName: '', clientId: 0, status: 'planned' });

  useEffect(() => {
    setForm(
      initial
        ? {
            projectName: initial.projectName,
            clientId: initial.clientId,
            startDate: initial.startDate ?? undefined,
            endDate: initial.endDate ?? undefined,
            regionId: initial.regionId,
            stateId: initial.stateId,
            status: initial.status,
          }
        : { projectName: '', clientId: 0, status: 'planned' },
    );
  }, [initial, open]);

  const set = <K extends keyof CreateProjectInput>(k: K, v: CreateProjectInput[K]) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? `Edit Project — ${initial?.projectCode}` : 'Create Project'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Project Name" value={form.projectName} onChange={(e) => set('projectName', e.target.value)} required fullWidth />
          <TextField
            select
            label="Client"
            value={form.clientId || ''}
            onChange={(e) => set('clientId', Number(e.target.value))}
            disabled={isEdit}
            helperText={isEdit ? 'Client cannot be changed after creation' : undefined}
            required
            fullWidth
          >
            {(clients?.data ?? []).map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.clientName} ({c.clientCode})</MenuItem>
            ))}
          </TextField>
          <Stack direction="row" spacing={2}>
            <TextField label="Start Date" type="date" InputLabelProps={{ shrink: true }} value={form.startDate ?? ''} onChange={(e) => set('startDate', e.target.value || undefined)} fullWidth />
            <TextField label="End Date" type="date" InputLabelProps={{ shrink: true }} value={form.endDate ?? ''} onChange={(e) => set('endDate', e.target.value || undefined)} fullWidth />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Region ID" type="number" value={form.regionId ?? ''} onChange={(e) => set('regionId', e.target.value ? Number(e.target.value) : null)} fullWidth />
            <TextField label="State ID" type="number" value={form.stateId ?? ''} onChange={(e) => set('stateId', e.target.value ? Number(e.target.value) : null)} fullWidth />
          </Stack>
          <TextField select label="Status" value={form.status} onChange={(e) => set('status', e.target.value as ProjectStatus)} fullWidth>
            {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSubmit(form)} disabled={!form.clientId}>{isEdit ? 'Save' : 'Create'}</Button>
      </DialogActions>
    </Dialog>
  );
}
